import {Injectable} from '@angular/core';
import {Actions, Effect, ofType} from '@ngrx/effects';
import {catchError, exhaustMap, switchMap} from 'rxjs/operators';
import {from, of} from 'rxjs';
import {LoadError} from '@app/core/store/actions/error.actions';
import {
	EStatisticsActions,
	GetRevenueStatistics,
	GetRevenueStatisticsSuccess,
	GetSalaryExpenseStatistics,
	GetSalaryExpenseStatisticsSuccess,
	GetSalaryPercentage,
	GetSalaryPercentageSuccess,
	GetStatistics,
	GetStatisticsSuccess,
	PatchTurnover,
	PatchTurnoverSuccess,
} from '@app/core/store/actions/statistics.actions';
import {IStatistics, StatisticService} from '@app/core/services/api/statistic.service';
import {getCurrentState} from '@app/core/helpers';
import {IAppState} from '@app/core/store/state/app.state';
import {Action, Store} from '@ngrx/store';
import {ISalaryPercentageModel} from '@app/core/models/salary-percentage-live.model';
import {ISalaryExpenseStatisticsModel} from '@app/core/models/salary-expense-statistics.model';
import {IRevenueStatisticsModel} from '@app/core/models/statistics-revenue.model';
import * as moment from 'moment';

@Injectable()
export class StatisticsEffects {

	@Effect()
	public onGetStatistics$ = this.actions$.pipe(
		ofType<GetStatistics>(EStatisticsActions.GetStatistics),
		exhaustMap((action: GetStatistics) => from(this.statisticService.getStatistics(action.start_date, action.end_date, getCurrentState(this.store).shiftDrafts.selectedShiftDraft)).pipe(
			exhaustMap((statistics: IStatistics) => [
				new GetStatisticsSuccess(statistics),
			]),
			catchError(error => of(new LoadError(error, action))),
		)),
	);
	@Effect()
	public onPatchTurnover$ = this.actions$.pipe(
		ofType<PatchTurnover>(EStatisticsActions.PatchTurnover),
		switchMap((action: PatchTurnover) => from(this.statisticService.patchTurnover(action.date, action.data)).pipe(
			switchMap(() => [
				new PatchTurnoverSuccess(action.date, action.data),
			]),
			catchError(error => of(new LoadError(error, action))),
		)),
	);
	@Effect()
	public onGetSalaryPercentage$ = this.actions$.pipe(
		ofType<GetSalaryPercentage>(EStatisticsActions.GetSalaryPercentage),
		exhaustMap((action: GetSalaryPercentage) => from(this.statisticService.getSalaryPercentage()).pipe(
			exhaustMap((data: ISalaryPercentageModel[]) => [
				new GetSalaryPercentageSuccess(data),
			]),
			catchError(error => of(new LoadError(error, action))),
		)),
	);

	@Effect()
	public onGetSalaryExpenseStatistics$ = this.actions$.pipe(
		ofType<GetSalaryExpenseStatistics>(EStatisticsActions.GetSalaryExpenseStatistics),
		exhaustMap((action: GetSalaryExpenseStatistics) => from(this.statisticService.getSalaryExpenseStatistics(action.from, action.to, action.filters)).pipe(
			exhaustMap((data: ISalaryExpenseStatisticsModel[]) => {

				const actions: Action[] = [];

				actions.push(new GetSalaryExpenseStatisticsSuccess(data));

				if (action.includeRevenue) {
					actions.push(new GetRevenueStatistics(moment(action.from), moment(action.to), {departments: action.filters.departments}));
				}

				return actions;
			}),
			catchError(error => of(new LoadError(error, action))),
		)),
	);
	@Effect()
	public onGetRevenueStatistics$ = this.actions$.pipe(
		ofType<GetRevenueStatistics>(EStatisticsActions.GetRevenueStatistics),
		exhaustMap((action: GetRevenueStatistics) => from(this.statisticService.getRevenue(action.from, action.to, action.filters)).pipe(
			exhaustMap((data: IRevenueStatisticsModel[]) => [
				new GetRevenueStatisticsSuccess(data),
			]),
			catchError(error => of(new LoadError(error, action))),
		)),
	);

	constructor(
		private actions$: Actions,
		private statisticService: StatisticService,
		private store: Store<IAppState>,
	) {
	}
}
